import React, { useState } from 'react';
import { useAuth } from '../../AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import './AuthPage.css';
import '../../createAdmin';
import '../../createNewAdmin';

function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, signUp, resetPassword, currentUser } = useAuth();
  const returnTo = location.state?.from?.pathname || '/';
  
  // Sign In form state
  const [signInEmail, setSignInEmail] = useState('');
  const [signInPassword, setSignInPassword] = useState('');
  const [signInError, setSignInError] = useState('');
  const [signInMessage, setSignInMessage] = useState('');

  // Sign Up form state
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpError, setSignUpError] = useState('');
  const [signUpMessage, setSignUpMessage] = useState('');
  const [adminMessage, setAdminMessage] = useState('');

  // Password Reset form state
  const [resetEmail, setResetEmail] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setSignInError('');
    setSignInMessage('');

    try {
      await signIn(signInEmail, signInPassword);
      setSignInMessage('Logged in successfully!');
      navigate(returnTo);
    } catch (err) {
      if (err.code === 'auth/wrong-password') {
        setSignInError('Incorrect password. Please try again.');
      } else if (err.code === 'auth/user-not-found') {
        setSignInError('No user found with this email.');
      } else if (err.code === 'auth/invalid-email') {
        setSignInError('The email address is not valid.');
      } else {
        setSignInError(`Failed to sign in: ${err.message}`);
      }
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setSignUpError('');
    setSignUpMessage('');

    try {
      await signUp(signUpEmail, signUpPassword);
      setSignUpMessage('Account created successfully! You are now logged in.');
      navigate(returnTo);
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setSignUpError('This email is already in use. Please sign in instead.');
      } else if (err.code === 'auth/weak-password') {
        setSignUpError('Password should be at least 6 characters.');
      } else if (err.code === 'auth/invalid-email') {
        setSignUpError('The email address is not valid.');
      } else {
        setSignUpError(`Failed to sign up: ${err.message}`);
      }
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetMessage('');

    try {
      await resetPassword(resetEmail);
      setResetMessage('Password reset email sent! Check your inbox.');
      setResetEmail('');
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        setResetError('No user found with this email address.');
      } else if (err.code === 'auth/invalid-email') {
        setResetError('The email address is not valid.');
      } else {
        setResetError(`Failed to send reset email: ${err.message}`);
      }
    }
  };

  const createAdminProfile = async () => {
    setAdminMessage('');
    try {
      // Check if user is authenticated
      if (!currentUser) {
        setAdminMessage('❌ Please sign in first');
        return;
      }

      // SECURITY: Only allow specific emails to become admin
      const authorizedAdminEmails = [
        'brona.nilsson@gmail.com',  // Brona's email
        'robbie@ideafarm.se',       // Robbie's email
        // Add more emails as needed
      ];

      if (!authorizedAdminEmails.includes(currentUser.email)) {
        setAdminMessage('❌ Unauthorized: Only specific users can create admin profiles');
        console.log('Unauthorized admin creation attempt by:', currentUser.email);
        return;
      }

      console.log('Authorized user creating admin profile:', currentUser.email);
      
      // Create admin profile directly in Firestore
      await setDoc(doc(db, 'users', currentUser.uid), {
        email: currentUser.email,
        role: 'admin',
        isMember: true,
        membershipDate: new Date(),
        createdAt: new Date()
      });
      
      setAdminMessage('✅ Admin profile created! Please refresh and try /admin');
      console.log('Admin profile created successfully');
    } catch (error) {
      console.error('Error creating admin profile:', error);
      setAdminMessage(`❌ Error: ${error.message}`);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-forms">
          {/* Sign In Form */}
          <div className="auth-form">
            <h2>LOG IN</h2>
            <form onSubmit={handleSignIn}>
              <div className="form-group">
                <input
                  type="email"
                  value={signInEmail}
                  onChange={(e) => setSignInEmail(e.target.value)}
                  placeholder="Email"
                  required
                />
              </div>
              <div className="form-group">
                <input
                  type="password"
                  value={signInPassword}
                  onChange={(e) => setSignInPassword(e.target.value)}
                  placeholder="Password"
                  required
                />
              </div>
              {signInError && <p className="error-message">{signInError}</p>}
              {signInMessage && <p className="success-message">{signInMessage}</p>}
              <button type="submit" className="auth-button">
                LOG IN
              </button>
              <button 
                type="button" 
                className="forgot-password-link"
                onClick={() => setShowResetForm(!showResetForm)}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: '#666', 
                  textDecoration: 'underline', 
                  cursor: 'pointer',
                  fontSize: '14px',
                  marginTop: '10px'
                }}
              >
                Forgot Password?
              </button>
            </form>
            
            {/* Password Reset Form */}
            {showResetForm && (
              <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f9f9f9', border: '1px solid #ddd' }}>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Reset Password</h3>
                <form onSubmit={handlePasswordReset}>
                  <div className="form-group">
                    <input
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                  {resetError && <p className="error-message">{resetError}</p>}
                  {resetMessage && <p className="success-message">{resetMessage}</p>}
                  <button type="submit" className="auth-button" style={{ fontSize: '14px', padding: '8px 16px' }}>
                    SEND RESET EMAIL
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setShowResetForm(false)}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      color: '#666', 
                      textDecoration: 'underline', 
                      cursor: 'pointer',
                      fontSize: '12px',
                      marginLeft: '10px'
                    }}
                  >
                    Cancel
                  </button>
                </form>
              </div>
            )}
          </div>

          {/* Sign Up Form */}
          <div className="auth-form">
            <h2>SIGN UP</h2>
            <form onSubmit={handleSignUp}>
              <div className="form-group">
                <input
                  type="email"
                  value={signUpEmail}
                  onChange={(e) => setSignUpEmail(e.target.value)}
                  placeholder="Email"
                  required
                />
              </div>
              <div className="form-group">
                <input
                  type="password"
                  value={signUpPassword}
                  onChange={(e) => setSignUpPassword(e.target.value)}
                  placeholder="Password (min 6 characters)"
                  required
                />
              </div>
              {signUpError && <p className="error-message">{signUpError}</p>}
              {signUpMessage && <p className="success-message">{signUpMessage}</p>}
              <button type="submit" className="auth-button">
                SIGN UP
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthPage;
